import { z } from 'zod';
import { ContradictionResolutionKindSchema } from './entities.js';

/**
 * Case Wiki — payloads des actions d'édition utilisateur (PR6 / TICKET-124).
 *
 * Distincts des tools agent (lint/review) — ces payloads sont consommés par
 * les routes REST `/api/cases/:caseId/wiki/{documents,contradictions,parties}/...`
 * et les hooks frontend (useCaseWikiEdit).
 */

export const SetDocumentPinnedRequestSchema = z.object({
  pinned: z.boolean(),
});
export type SetDocumentPinnedRequest = z.infer<typeof SetDocumentPinnedRequestSchema>;

export const SetDocumentUserNoteRequestSchema = z.object({
  /** null pour effacer la note. */
  note: z.string().max(2000).nullable(),
});
export type SetDocumentUserNoteRequest = z.infer<typeof SetDocumentUserNoteRequestSchema>;

export const ResolveContradictionByUserRequestSchema = z.object({
  resolution_kind: ContradictionResolutionKindSchema,
  resolved_note: z.string().min(10).max(1000),
});
export type ResolveContradictionByUserRequest = z.infer<
  typeof ResolveContradictionByUserRequestSchema
>;

/**
 * PR8 — Création d'une chronologie custom par l'utilisateur.
 * `prompt` (10-1000 chars) décrit l'intention ("tous les faits autour de la
 * livraison de mars 2024"). En V1 le filtrage agentique n'est pas exécuté —
 * la chronologie est stockée avec son prompt et l'utilisateur peut éditer
 * `fact_ids` manuellement plus tard. PR8 V2 ajoutera l'exécution LLM.
 */
export const CreateCustomChronologyRequestSchema = z.object({
  name: z.string().min(3).max(200),
  description: z.string().max(500).optional(),
  prompt: z.string().min(10).max(1000),
  fact_ids: z.array(z.string().uuid()).max(500).optional(),
  procedure_id: z.string().uuid().optional(),
});
export type CreateCustomChronologyRequest = z.infer<
  typeof CreateCustomChronologyRequestSchema
>;

/**
 * PATCH /api/cases/:caseId/wiki/chronologies/:id
 *
 * Tous les champs sont optionnels — le serveur ne touche que ceux fournis.
 * Si `prompt` est modifié, le serveur DOIT déclencher un rerun de l'agent
 * preview pour rafraîchir `fact_ids` (cf. DEBT-133 §"Comportement PATCH").
 *
 * `fact_ids` peut être passé directement (édition manuelle des facts par
 * l'utilisateur) — dans ce cas pas de rerun LLM, on persiste la liste fournie.
 */
export const UpdateChronologyRequestSchema = z.object({
  name: z.string().min(3).max(200).optional(),
  description: z.string().max(500).nullable().optional(),
  prompt: z.string().min(10).max(1000).optional(),
  fact_ids: z.array(z.string().uuid()).max(500).optional(),
});
export type UpdateChronologyRequest = z.infer<typeof UpdateChronologyRequestSchema>;
